<template>
    <div id="home" class="page-content">
        <section
            id="header"
            :class="$mq"
            data-aos="fade-in"
            data-aos-duration="700"
            data-aos-easing="ease-in-out"
            data-aos-mirror="true"
        >
            <div class="title">
                <img
                    src="@/assets/images/hand-logo.png"
                    alt="logo"
                    class="logo"
                />
                <div>
                    <base-text type="h2" class="subheading">
                        {{ data.subheading }}
                    </base-text>
                    <base-text type="h1">
                        {{ data.heading }}
                    </base-text>
                    <router-link to="/portfolio">
                        <base-text type="h4">
                            {{ data.portfolio_link_label }}
                        </base-text>
                    </router-link>
                </div>
            </div>

            <base-text v-if="data.msg" type="h3" class="msg">
                {{ data.msg }}
            </base-text>

            <div class="time-place">
                <div class="time">
                    <base-text type="h3">
                        When?
                    </base-text>
                    <base-text type="p">
                        {{ date }}
                        <br />
                        {{ start }} - {{ end }}
                    </base-text>
                    <add-to-calendar />
                </div>

                <div class="place">
                    <base-text type="h3">
                        Where?
                    </base-text>
                    <base-text type="p">
                        {{ data.location }}
                    </base-text>
                    <base-button
                        type="a"
                        :href="data.tickets"
                        target="_blank"
                        rel="noopener"
                        design="primary"
                        :disabled="true"
                    >
                        Get Tickets
                    </base-button>
                </div>
            </div>

            <button class="scroll-btn" @click="scrollDown">
                <fa-icon
                    :icon="['fas', 'chevron-down']"
                    aria-label="scroll down"
                />
                <div id="home-scroll-to" />
            </button>
        </section>

        <section
            id="about"
            :class="$mq"
            data-aos="fade-in"
            data-aos-duration="700"
            data-aos-easing="ease-in-out"
            data-aos-mirror="true"
        >
            <base-window class="about-video">
                <video autoplay muted loop>
                    <source
                        src="@/assets/videos/2019-video.webm"
                        type="video/webm"
                    />
                    <source
                        src="@/assets/videos/2019-video.mp4"
                        type="video/mp4"
                    />
                </video>
            </base-window>

            <div class="content">
                <base-text type="h3">
                    {{ data.about.title }}
                </base-text>
                <base-text type="p">
                    {{ data.about.content }}
                </base-text>
                <base-button type="router-link" to="/sponsors" design="primary">
                    Sponsor Us
                </base-button>
            </div>
        </section>

        <section
            id="details"
            :class="$mq"
            data-aos="fade-in"
            data-aos-duration="700"
            data-aos-easing="ease-in-out"
            data-aos-mirror="true"
        >
            <div
                class="detail"
                v-for="(detail, index) in data.details"
                :key="index"
            >
                <img class="icon" :src="detail.icon" alt="detail icon" />
                <div class="content">
                    <base-text type="h3">
                        {{ detail.title }}
                    </base-text>
                    <base-text type="p">
                        {{ detail.description }}
                    </base-text>
                </div>
            </div>
        </section>

        <section v-if="data.sponsors.length > 0" id="sponsors">
            <base-text type="h3">
                Sponsors
            </base-text>
            <carousel
                class="carousel"
                :autoplay="true"
                :loop="true"
                :perPage="5"
                :autoplayHoverPause="false"
                :paginationEnabled="false"
                :speed="1500"
                :autoplayTimeout="3000"
            >
                <slide
                    class="slide"
                    v-for="(sponsor, index) in data.sponsors"
                    :key="index"
                >
                    <img
                        class="sponsor-logo"
                        :src="sponsor.logo"
                        :alt="sponsor.name"
                    />
                </slide>
            </carousel>
            <base-button
                type="router-link"
                to="/sponsors"
                design="primary"
                class="sponsors-btn"
            >
                Sponsor Us
            </base-button>
        </section>
    </div>
</template>

<script>
    // CMS DATA LOADING
    import dataLoader from '../mixins/dataLoader';
    import data from '../content/pages/home.md';
    import moment from 'moment';
    import { Carousel, Slide } from 'vue-carousel';
    import AddToCalendar from '../components/AddToCalendar';

    export default {
        name: 'home',

        mixins: [dataLoader(data)],

        components: {
            Carousel,
            Slide,
            AddToCalendar,
        },

        computed: {
            date() {
                return moment(this.data.time.start).format('MMMM Do YYYY');
            },

            start() {
                return moment(this.data.time.start).format('h:mm a');
            },

            end() {
                return moment(this.data.time.end).format('h:mm a');
            },
        },

        methods: {
            scrollDown() {
                const el = document.getElementById('home-scroll-to');
                el.scrollIntoView();
            },
        },
    };
</script>

<style lang="scss" scoped>
    section#header {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-evenly;
        padding: 0 100px;

        .title {
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: 900px;

            .logo {
                width: 150px;
                margin-right: $standard-space;
            }

            .subheading {
                margin-bottom: 10px;
            }

            a {
                margin-top: 25px;
                color: white;
                display: inline-block;
                border-bottom: 2px solid white;
                text-decoration: none;

                .h4 {
                    font-size: 20px;
                }
            }
        }

        .msg {
            text-align: center;
        }

        .time-place {
            display: flex;

            .time,
            .place {
                padding: 30px 40px;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                width: 300px;

                h3,
                p {
                    margin-bottom: 30px;
                }
            }

            .time {
                text-align: right;
                align-items: flex-end;
                border-right: 2px solid #fff;
            }
        }

        .scroll-btn {
            background: none;
            border: 0;
            outline: none;
            color: #fff;
            font-size: 30px;
            cursor: pointer;

            &:hover {
                transform: translateY(5px);
            }
        }

        &.mobile,
        &.tablet {
            padding: 0;

            .title {
                flex-direction: column;
                text-align: center;
                margin: $standard-space 0;

                .logo {
                    margin-right: 0;
                    width: 50px;
                    margin-bottom: $standard-space-mobile;
                }
            }

            .time-place {
                margin-bottom: $standard-space;
            }

            .scroll-btn {
                margin-bottom: $standard-space;
            }
        }

        &.mobile {
            .time-place {
                flex-direction: column;
                margin-bottom: $standard-space;

                .time,
                .place {
                    align-items: center;
                    text-align: center;
                    padding: 0;
                    width: 100%;

                    h3,
                    p {
                        margin-bottom: 10px;
                    }
                }

                .time {
                    border-right: 0;
                    margin-bottom: $standard-space-mobile;
                }
            }
        }
    }

    section:not(#header) {
        margin-bottom: 200px;

        &.tablet {
            margin-bottom: 100px;
        }

        &.mobile {
            margin-bottom: 50px;
        }
    }

    section#about {
        display: flex;
        align-items: center;
        justify-content: center;

        .about-video {
            width: 60%;

            video {
                width: 100%;
            }
        }

        .content {
            width: 40%;
            margin-left: 100px;

            h3,
            p {
                margin-bottom: $standard-space;
            }
        }

        &.tablet,
        &.mobile {
            flex-direction: column;

            .about-video {
                width: 100%;
            }

            .content {
                width: 100%;
                margin-left: 0;
                text-align: center;
                display: flex;
                flex-direction: column;
                align-items: center;
            }
        }

        &.tablet {
            .about-video {
                margin-bottom: $standard-space;
            }
        }

        &.mobile {
            .about-video {
                margin-bottom: $standard-space-mobile;
            }

            .content {
                h3,
                p {
                    margin-bottom: $standard-space-mobile;
                }
            }
        }
    }

    section#details {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        padding: 0 100px;

        .detail {
            width: calc(50% - #{$standard-space});
            display: flex;
            align-items: center;

            &:not(:nth-last-of-type(-n + 2)) {
                margin-bottom: $standard-space;
            }

            &:nth-of-type(odd) {
                margin-right: $standard-space;
            }

            .icon {
                width: 25%;
            }

            .content {
                margin-left: 30px;

                h3 {
                    margin-bottom: 15px;
                }
            }
        }

        &.tablet,
        &.mobile {
            padding: 0;

            .detail {
                margin-right: 0;
                width: 100%;

                &:nth-of-type(odd) {
                    flex-direction: row-reverse;
                    .content {
                        text-align: right;
                        margin-left: 0;
                        margin-right: 30px;
                    }
                }
            }
        }

        &.tablet {
            .detail {
                margin-bottom: $standard-space;
            }
        }

        &.mobile {
            .detail {
                margin-bottom: $standard-space-mobile;
            }
        }
    }

    section#sponsors {
        display: flex;
        flex-direction: column;
        align-items: center;

        h3 {
            margin-bottom: $standard-space;
        }

        .carousel {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
        }

        .slide {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .sponsor-logo {
            width: 150px;
        }

        .sponsors-btn {
            margin-top: $standard-space;
        }
    }
</style>
